// src/components/dashboard/pages-dashboard/UserJugs.jsx - VERSIÓN COMPLETA CON LÓGICA DE RECAMBIOS MÚLTIPLES
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Badge, Button, Separator, Text } from "@radix-ui/themes";
import {
  ChevronDown,
  MapPin,
  RefreshCw,
  AlertTriangle,
  Plus,
  ShoppingCart,
  Droplets,
  Ellipsis,
  EllipsisIcon,
} from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useUserJugs } from "../../../hooks/useUserJugs";
import JugCard from "../pages-components/JugCard";
import RefillCard from "../pages-components/RefillCard";
import LoadingScreen from "../../ui/LoadingScreen";
import Compare from "../../landingPage/Compare";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Componente de barra fija para recambios
const RefillBottomBar = React.memo(
  ({ selectedJugs, jugsData, onProcessRefills, isProcessing }) => {
    const navigate = useNavigate();

    // Precio de recambio (ajustar según tu configuración)
    const REFILL_PRICE = 15000;

    // Calcular datos de los recambios seleccionados
    const refillData = useMemo(() => {
      // Asegurarnos de que selectedJugs sea un array
      const selectedArray =
        selectedJugs instanceof Set
          ? [...selectedJugs]
          : Array.isArray(selectedJugs)
          ? selectedJugs
          : [];

      const selectedJugDetails = selectedArray.map((jugId) => {
        const jug = jugsData.find((j) => j.jug_id === jugId);
        return {
          jugId: jug.jug_id,
          barcode: jug.barcode,
          addressId: jug.delivery_address_id,
          address: jug.address,
          city: jug.city,
          price: REFILL_PRICE,
          // Agregar más información relevante del botellón
          created_at: jug.created_at,
          detail_type: jug.detail_type,
          order_detail_status: jug.order_detail_status,
          latitude: jug.latitude,
          longitude: jug.longitude,
          user_id: jug.user_id,
          address_image_url: jug.image_url,
        };
      });

      const subtotal = selectedJugDetails.length * REFILL_PRICE;

      return {
        items: selectedJugDetails,
        count: selectedJugDetails.length,
        subtotal,
        subtotalFormatted: new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 0,
        }).format(subtotal),
      };
    }, [selectedJugs, jugsData]);

    // Estados del sidebar
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
      const handleResize = () => {
        const newIsMobile = window.innerWidth < 768;
        setIsMobile(newIsMobile);

        if (newIsMobile) {
          setSidebarOpen(false);
        } else {
          setSidebarOpen(true);
        }
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Handler para navegar a NewRefills
    const handleNavigateToRefills = () => {
      // Asegurarnos de que selectedJugs sea un Set
      const selectedSet =
        selectedJugs instanceof Set ? selectedJugs : new Set(selectedJugs);

      // Navegar a la nueva ruta con los datos de los recambios
      navigate("/recambios", {
        state: {
          refillData: refillData,
          jugsData: jugsData.filter((jug) => selectedSet.has(jug.jug_id)),
        },
      });
    };

    if (refillData.count === 0) return null;

    return (
      <div
        className="fixed bottom-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30 transition-all duration-300 ease-in-out"
        style={{
          left: isMobile ? "0px" : sidebarOpen ? "256px" : "70px",
        }}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <p className="font-medium text-gray-900">
              {refillData.count} Botell{refillData.count > 1 ? "ones" : "ón"}{" "}
              seleccionado{refillData.count > 1 ? "s" : ""}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Botón principal para ir al nuevo flujo */}
            <Button
              size="3"
              className="bg-gray-100 hover:bg-gray-200"
              onClick={handleNavigateToRefills}
              disabled={isProcessing}
            >
              <EllipsisIcon color="#222222" />
            </Button>
            <Button
              size="3"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleNavigateToRefills}
              disabled={isProcessing}
            >
              Pedir recambios
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

// Componente para el header mejorado
const JugsHeader = React.memo(
  ({ totalJugs, alertsCount, hasAlerts, isRefreshing, onRefresh }) => {
    return (
      <>
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-8">
          <div className="flex space-x-4 w-full items-center">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Droplets className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-medium text-gray-900 mb-0 pb-0">
                Cuentas con {totalJugs}{" "}
                {totalJugs === 1 ? "botellón" : "botellones"}
              </h1>
              <div className="flex items-center gap-3 text-base text-gray-600">
                {hasAlerts && (
                  <div className="flex items-center gap-1 text-orange-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      {alertsCount.total} alerta
                      {alertsCount.total !== 1 ? "s" : ""}
                    </span>
                  </div>
                )}
              </div>
            </div>{" "}
          </div>
          {/* Columna de estadísticas y métricas */}
            <div className=" flex flex-col items-start md:items-end justify-center">
              <a href="/nuevo-pedido">
                <button className="text-lg fraunces font-bold bg-blue-600 text-stone-50 rounded-xl px-4 py-2 text-nowrap">
                  Pedir nuevo botellón
                </button>
              </a>
            </div>

        </div>
      </>
    );
  }
);

// Componente memoizado para cuando no hay botellones
const EmptyJugsView = React.memo(() => {
  return (
    <div className="min-h-[60vh] flex flex-col justify-center items-center px-4">
      <div className="max-w-6xl w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12">
          <div className="text-center lg:text-left">
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight mb-8 text-gray-900 fraunces">
              Pide hoy <br />
              <span className="text-blue-600 font-bold">tu botellón</span>
              <br />y asegura tu suministro
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto lg:mx-0">
              Agua purificada de calidad premium con entrega a domicilio y
              sistema de recambios sustentable.
            </p>
            <Button
              size="4"
              className="text-lg px-8 py-3 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              <a href="/nuevo-pedido" className="text-white no-underline">
                Pedir mi primer botellón
              </a>
            </Button>
          </div>

          <div className="flex justify-center">
            <img
              src="https://vacewaenygdaqchlrsam.supabase.co/storage/v1/object/public/dashboard-publics/new-botellon-plan-recambios-botellon-20l-dispensador-pedidos-online-entrega-domicilio-agua-purificada-calidad.png"
              className="w-full max-w-md"
              alt="Botellón y dispensador"
            />
          </div>
        </div>

        <Collapsible.Root className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <Collapsible.Trigger className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                ¿Por qué elegir Fluenti?
              </h3>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </div>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <div className="px-6 pb-6">
              <Compare container={false} />
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      </div>
    </div>
  );
});

// Componente para carousel horizontal de botellones
const JugsCarousel = React.memo(
  ({
    jugs,
    refillPairs = [],
    onJugSelect,
    onPayment,
    selectedJugs,
    canSelect,
    canRequestRefill,
    isJugInRefillPair,
  }) => {
    const allItems = [...refillPairs, ...jugs];

    if (allItems.length === 0) return null;

    return (
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory">
          {/* Render RefillCards primero - NO SELECCIONABLES */}
          {refillPairs.map((pair) => (
            <div
              key={`refill-${pair.oldJug.jug_id}-${pair.newJug.jug_id}`}
              className="flex-shrink-0 snap-start"
            >
              <RefillCard
                oldJug={pair.oldJug}
                newJug={pair.newJug}
                isSelected={false} // Siempre false
                onSelection={() => {}} // Función vacía
                onPayment={onPayment}
                canSelect={false} // Siempre false
              />
            </div>
          ))}

          {/* Render JugCards normales */}
          {jugs.map((jug) => {
            const isEligible = canRequestRefill(jug);
            const isInRefillPair = isJugInRefillPair(jug.jug_id, refillPairs);
            const isSelectable = isEligible && !isInRefillPair;

            return (
              <div
                key={`${jug.jug_id}-${jug.order_detail_status}`}
                className="flex-shrink-0 snap-start"
              >
                <JugCard
                  jug={jug}
                  isSelected={selectedJugs.has(jug.jug_id)}
                  onSelection={(jugId) =>
                    onJugSelect(jugId, isEligible, isInRefillPair)
                  }
                  onPayment={onPayment}
                  canSelect={isSelectable}
                  isEligible={isEligible}
                  isInRefillPair={isInRefillPair}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

// Componente principal UserJugs
const UserJugs = React.memo(() => {
  // Estados existentes
  const [selectedJugs, setSelectedJugs] = useState(new Set());
  const [isProcessingRefills, setIsProcessingRefills] = useState(false);

  // Hooks existentes
  const {
    jugsData,
    isLoading,
    error,
    hasJugs,
    refreshData,
    isRefreshing,
    canRequestRefill,
    requestRefill,
    processPayment,
    groupedByAddress,
    totalJugs,
    alertsCount,
    hasAlerts,
  } = useUserJugs();

  // Nuevo hook para órdenes

  // Función para identificar pares de refill (sin cambios)
  const identifyRefillPairs = useCallback((jugs) => {
    const refillPairs = [];
    const processedJugs = new Set();

    // Buscar recambios con returned_jug_id
    jugs.forEach((jug) => {
      if (
        jug.detail_type === "refill" &&
        jug.previous_jug_id &&
        !processedJugs.has(jug.jug_id)
      ) {
        // Buscar el botellón viejo que se está retornando
        const oldJug = jugs.find(
          (j) =>
            j.jug_id === jug.previous_jug_id && !processedJugs.has(j.jug_id)
        );

        if (oldJug) {
          refillPairs.push({
            oldJug: oldJug,
            newJug: jug,
            refillId: `${oldJug.jug_id}-${jug.jug_id}`,
          });

          // Marcar ambos como procesados para no mostrarlos como individuales
          processedJugs.add(oldJug.jug_id);
          processedJugs.add(jug.jug_id);
        }
      }
    });

    // Retornar tanto los pares como los botellones no procesados
    const remainingJugs = jugs.filter((jug) => !processedJugs.has(jug.jug_id));

    return { refillPairs, remainingJugs };
  }, []);

  // Función para verificar si un botellón está en un par de refill (sin cambios)
  const isJugInRefillPair = useCallback((jugId, refillPairs) => {
    return refillPairs.some(
      (pair) => pair.oldJug.jug_id === jugId || pair.newJug.jug_id === jugId
    );
  }, []);

  // Botellones elegibles para recambio (sin cambios)
  const eligibleJugs = useMemo(() => {
    const allRefillPairs = Object.values(groupedByAddress).flatMap(
      (addressData) => identifyRefillPairs(addressData.jugs).refillPairs
    );

    return jugsData.filter((jug) => {
      const isEligible = canRequestRefill(jug);
      const isInPair = isJugInRefillPair(jug.jug_id, allRefillPairs);
      return isEligible && !isInPair;
    });
  }, [
    jugsData,
    canRequestRefill,
    groupedByAddress,
    identifyRefillPairs,
    isJugInRefillPair,
  ]);

  // Botellones seleccionados elegibles (sin cambios)
  const selectedEligibleJugs = useMemo(() => {
    return [...selectedJugs].filter((jugId) =>
      eligibleJugs.some((jug) => jug.jug_id === jugId)
    );
  }, [selectedJugs, eligibleJugs]);

  // Handlers existentes
  const handleJugSelection = useCallback(
    (jugId, isEligible, isInRefillPair = false) => {
      // No permitir selección si:
      // - No es elegible
      // - Está en un par de refill
      if (!isEligible || isInRefillPair) return;

      setSelectedJugs((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(jugId)) {
          newSet.delete(jugId);
        } else {
          newSet.add(jugId);
        }
        return newSet;
      });
    },
    []
  );

  const handleSelectAll = useCallback(() => {
    if (selectedEligibleJugs.length === eligibleJugs.length) {
      setSelectedJugs(new Set());
    } else {
      setSelectedJugs(new Set(eligibleJugs.map((jug) => jug.jug_id)));
    }
  }, [selectedEligibleJugs.length, eligibleJugs]);

  // Handler para procesar recambios individuales (sin cambios)
  const handleProcessRefills = useCallback(async () => {
    if (selectedEligibleJugs.length === 0) return;

    setIsProcessingRefills(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const jugId of selectedEligibleJugs) {
        const jug = jugsData.find((j) => j.jug_id === jugId);
        if (jug && canRequestRefill(jug)) {
          try {
            const success = await requestRefill(
              jug.jug_id,
              jug.delivery_address_id
            );
            if (success) successCount++;
            else errorCount++;
          } catch {
            errorCount++;
          }
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} recambio${successCount > 1 ? "s" : ""} solicitado${
            successCount > 1 ? "s" : ""
          } exitosamente`,
          { duration: 4000 }
        );
      }

      if (errorCount > 0) {
        toast.error(
          `Error en ${errorCount} recambio${errorCount > 1 ? "s" : ""}`,
          { duration: 4000 }
        );
      }

      // Limpiar selección
      setSelectedJugs(new Set());
    } catch (error) {
      console.error("Error processing refills:", error);
      toast.error("Error al procesar los recambios");
    } finally {
      setIsProcessingRefills(false);
    }
  }, [selectedEligibleJugs, jugsData, canRequestRefill, requestRefill]);

  const handlePayment = useCallback(
    (jugId, amount) => {
      processPayment(jugId, amount);
    },
    [processPayment]
  );

  // Mostrar pantalla de carga (sin cambios)
  if (isLoading) {
    return <LoadingScreen message="Cargando tus botellones..." />;
  }

  // Mostrar error si existe (sin cambios)
  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            Error al cargar
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={refreshData} disabled={isRefreshing} size="3">
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 md:px-0">
      {!hasJugs ? (
        <EmptyJugsView />
      ) : (
        <div
          className={`space-y-6 ${
            selectedEligibleJugs.length > 0 ? "pb-24" : ""
          }`}
        >
          {/* Header mejorado */}
          <JugsHeader
            totalJugs={totalJugs}
            alertsCount={alertsCount}
            hasAlerts={hasAlerts}
            isRefreshing={isRefreshing}
            onRefresh={refreshData}
          />
          <Separator size="4" mb="6" />

          {/* Secciones de botellones agrupadas por dirección */}
          <div className="space-y-6">
            {Object.entries(groupedByAddress).map(
              ([addressKey, addressData]) => {
                // Aplicar categorización a los botellones de esta dirección
                const addressJugs = addressData.jugs;
                const { refillPairs, remainingJugs } =
                  identifyRefillPairs(addressJugs);

                return (
                  <div className="flex flex-col gap-4" key={addressKey}>
                    <div className="bg-white rounded-2xl p-6">
                      {/* Header de dirección */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                          <MapPin className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-lg md:text-lg font-semibold text-gray-900 text-ellipsis overflow-hidden line-clamp-2 ">
                            {addressData.address || "Dirección no especificada"}
                          </h2>
                          {addressData.city && (
                            <p className="text-sm text-gray-500">
                              {addressData.city}
                            </p>
                          )}
                        </div>
                        <div className="p-2 w-12 font-bold text-xl fraunces text-blue-600 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                          {addressJugs.length}
                        </div>
                      </div>
                    </div>
                    {/* Carousel horizontal de todos los botellones */}
                    <JugsCarousel
                      jugs={remainingJugs}
                      refillPairs={refillPairs}
                      onJugSelect={handleJugSelection}
                      onPayment={handlePayment}
                      selectedJugs={selectedJugs}
                      canSelect={true}
                      canRequestRefill={canRequestRefill}
                      isJugInRefillPair={isJugInRefillPair}
                    />
                    <hr className="my-4" />
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* RefillBottomBar - actualizado con nuevo handler */}
      {selectedEligibleJugs.length > 0 && (
        <RefillBottomBar
          selectedJugs={selectedEligibleJugs}
          jugsData={jugsData}
          isProcessing={isProcessingRefills}
        />
      )}
    </div>
  );
});

UserJugs.displayName = "UserJugs";

export default UserJugs;

RefillBottomBar.displayName = "RefillBottomBar";

EmptyJugsView.displayName = "EmptyJugsView";

JugsHeader.displayName = "JugsHeader";

JugsCarousel.displayName = "JugsCarousel";
